import { z } from "zod";

/** Connection providers that ingest and send email. */
export const EMAIL_PROVIDER_IDS = ["microsoft_outlook"] as const;

/** Schema for email connection provider identifiers. */
export const EmailProviderKeySchema = z.enum(EMAIL_PROVIDER_IDS);

/** A connection provider that ingests and sends email. */
export type EmailProviderKey = z.infer<typeof EmailProviderKeySchema>;

/**
 * Identifiers for first-party email ingestion providers. The platform polls
 * each provider directly (rather than via a third-party connector) to fetch
 * messages for an organization's shared inboxes. Extensible to gmail/imap_smtp.
 */
export const EmailProviderIdSchema = z.enum(["microsoft_graph"]);

/** A single first-party email ingestion provider identifier. */
export type EmailProviderId = z.infer<typeof EmailProviderIdSchema>;

/**
 * The same identifiers as named members, for value sites that would otherwise
 * repeat the bare string. {@link EmailProviderIdSchema} stays the parse-time
 * source of truth: deriving it from this enum would widen
 * {@link EmailProviderId} from a string literal to the enum type, so the two
 * must be kept in sync by hand.
 */
export enum EmailProviderIdValue {
  MICROSOFT_GRAPH = "microsoft_graph",
}

/**
 * Provider-specific incremental-sync cursor persisted per email source — the
 * "where did I leave off" marker the poller hands back to the provider each
 * cycle. Currently only Microsoft Graph's delta cursor; widen this to a
 * provider-discriminated union when additional providers are added (e.g. Gmail
 * `historyId`, IMAP `UIDVALIDITY` + last-seen UID).
 */
export const emailSourceCursorSchema = z.object({
  /** Microsoft Graph `@odata.deltaLink` URL used to fetch the next delta page. */
  deltaLink: z.string().optional(),
});

/** A persisted, provider-specific incremental-sync cursor for an email source. */
export type EmailSourceCursor = z.infer<typeof emailSourceCursorSchema>;

/**
 * Parses a persisted cursor value (raw DB JSON) into a typed
 * {@link EmailSourceCursor}, returning null when absent or malformed.
 * @param value - The raw cursor value read from storage
 * @returns The parsed cursor, or null when missing or invalid
 */
export const parseEmailSourceCursor = (
  value: unknown,
): EmailSourceCursor | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const parsed = emailSourceCursorSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

/** A provider-agnostic email address. */
export const emailAddressSchema = z.object({
  name: z.string().nullish(),
  address: z.string(),
});

/** A single email address, normalized across providers. */
export type EmailAddress = z.infer<typeof emailAddressSchema>;

/**
 * The canonical, provider-agnostic shape every email provider normalizes into.
 * Provider-specific extras (Gmail labels, IMAP flags, Outlook importance) live
 * in {@link baseEmailSchema.shape.providerMetadata} rather than as top-level
 * fields, so downstream consumers never branch on provider.
 */
export const baseEmailSchema = z.object({
  providerMessageId: z.string(),
  internetMessageId: z.string().nullish(),
  conversationId: z.string().nullish(),
  from: emailAddressSchema.nullish(),
  to: z.array(emailAddressSchema).default([]),
  cc: z.array(emailAddressSchema).default([]),
  bcc: z.array(emailAddressSchema).default([]),
  subject: z.string().nullish(),
  textBody: z.string().nullish(),
  htmlBody: z.string().nullish(),
  receivedAt: z.date(),
  inReplyTo: z.string().nullish(),
  references: z.array(z.string()).default([]),
  hasAttachments: z.boolean().default(false),
  providerMetadata: z.record(z.string(), z.unknown()).default({}),
  rawProviderPayload: z.string().nullish(),
});

/** A single email message normalized to the canonical cross-provider shape. */
export type BaseEmail = z.infer<typeof baseEmailSchema>;

/**
 * Provider-agnostic substring filters for the receive-email trigger. An email
 * is processed when it matches any provided filter.
 */
export const emailFilterSchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  cc: z.string().optional(),
  bcc: z.string().optional(),
  subject: z.string().optional(),
  body: z.string().optional(),
});

/** Substring filters applied to an incoming email before processing. */
export type EmailFilters = z.infer<typeof emailFilterSchema>;

/** Status of an email message in the ingestion queue pipeline. */
export const EmailMessageQueueStatusSchema = z.enum([
  "PENDING",
  "QUEUED",
  "SKIPPED",
  "FAILED",
]);

/** Queue status of a fetched email message awaiting publication. */
export type EmailMessageQueueStatus = z.infer<
  typeof EmailMessageQueueStatusSchema
>;

/**
 * Direction of an agent email message relative to the shared inbox: inbound
 * (received) or outbound (sent).
 */
export const EMAIL_MESSAGE_DIRECTIONS = {
  INBOUND: "inbound",
  OUTBOUND: "outbound",
} as const;

/**
 * Direction of an agent email message relative to the shared inbox.
 */
export const EmailMessageDirectionSchema = z.enum([
  EMAIL_MESSAGE_DIRECTIONS.INBOUND,
  EMAIL_MESSAGE_DIRECTIONS.OUTBOUND,
]);

/**
 * Direction of an agent email message: inbound (received) or outbound (sent).
 */
export type EmailMessageDirection = z.infer<typeof EmailMessageDirectionSchema>;

/**
 * Default direction applied to a persisted email message when none is supplied.
 * Ingestion only produces received mail, so it defaults to inbound.
 */
export const DEFAULT_EMAIL_MESSAGE_DIRECTION: EmailMessageDirection =
  EMAIL_MESSAGE_DIRECTIONS.INBOUND;
